import { z } from 'zod';
import { resourceVisibilitySchema } from './resource.js';

export const activityResourceTypeSchema = z.enum(['resource', 'category', 'user', 'announcement']);
export type ActivityResourceType = z.infer<typeof activityResourceTypeSchema>;

/**
 * Action codes are stable identifiers — never rename in place. If a new action
 * is introduced for an existing resource type, append it. The frontend resolves
 * action → label/icon via a lookup table; an unknown action falls back to a
 * generic "did something" line, never crashes.
 */
export const activityActionSchema = z.enum([
  'resource.create',
  'resource.update',
  'resource.delete',
  'resource.favorite',
  'resource.unfavorite',
  'category.create',
  'category.update',
  'category.delete',
  'user.create',
  'user.update',
  'user.delete',
  'announcement.create',
  'announcement.update',
  'announcement.delete',
]);
export type ActivityAction = z.infer<typeof activityActionSchema>;

/**
 * Visibility snapshot stored on each activity entry so the read endpoint can
 * filter without joining back against the resource doc (which may have been
 * deleted or had its visibility mutated since the event happened).
 *
 * Only relevant for resourceType === 'resource'. Null for everything else.
 */
export const activityVisibilitySnapshotSchema = z.object({
  visibility: resourceVisibilitySchema,
  creatorArea: z.string().nullable().default(null),
  sharedWith: z.array(z.string()).default([]),
  sharedWithAreas: z.array(z.string()).default([]),
  resourceCreatedBy: z.string().nullable().default(null),
});
export type ActivityVisibilitySnapshot = z.infer<typeof activityVisibilitySnapshotSchema>;

export const activityLogSchema = z.object({
  id: z.string(),
  actorId: z.string(),
  actorEmail: z.string(),
  actorName: z.string().nullable().default(null),
  action: activityActionSchema,
  resourceType: activityResourceTypeSchema,
  resourceId: z.string(),
  resourceTitle: z.string().nullable().default(null),
  // Visibility snapshot — only set when resourceType === 'resource'.
  scope: activityVisibilitySnapshotSchema.nullable().default(null),
  // Optional payload for action-specific metadata (changed fields, before/after,
  // etc.). Keep small — opaque to the timeline UI.
  metadata: z.record(z.unknown()).default({}),
  timestamp: z.string().datetime(),
});
export type ActivityLog = z.infer<typeof activityLogSchema>;

export const activityQuerySchema = z.object({
  limit: z.coerce.number().int().positive().max(100).optional().default(50),
  cursor: z.string().optional(),
  actorEmail: z.string().email().optional(),
  resourceType: activityResourceTypeSchema.optional(),
  action: activityActionSchema.optional(),
});
export type ActivityQuery = z.infer<typeof activityQuerySchema>;

export interface ActivityListResponse {
  items: ActivityLog[];
  nextCursor: string | null;
}
